/**
 * **The order a family's list comes back in** — the horizons, and the rank inside one
 * (issue #310).
 *
 * `get_backlog` has grouped and ranked the backlog since ADR 0006 decision 4, and until now it
 * was the only thing that showed a family their list, so the ordering lived beside the tool in
 * `shaping.ts`. The control panel's *What's on your plate* is the second surface, and its
 * acceptance criterion is that open items appear *"grouped by horizon, in the tool's own
 * order"* — which a comment cannot hold and a second sort function in a browser would quietly
 * stop honouring the first time a tie-break moved.
 *
 * **This is `CONTEXT_KIND_HEADING`'s argument about behaviour rather than about words.** Two
 * emitters, one rule, and a drift between them breaks no build and fails no test anybody
 * thought to write: a family reads their list in Claude, opens the panel, and finds the same
 * items in a different order with nothing to tell them which one is Boone's.
 *
 * **What deliberately stays out of here.** The *headings* — "Today", "This week", "Soon" — are
 * the panel's alone: `get_backlog` returns `groups` keyed by the raw `today | this_week |
 * later` and says none of those words to a family (its description glosses the enum for a
 * host, which is the schema explained to a model), so by `words.ts`'s rule — *words more than
 * one runtime must agree on* — there is nothing yet to share. They move here on the day a tool
 * speaks them.
 *
 * **`TimeHorizon` and `Priority` themselves stay in `@pilotfish/db`**, which is exactly the
 * call {@link CONTEXT_KIND_ORDER} made about `ContextType`: what has to be shared is the
 * *order*, not the union, and `shaping.ts` keeps annotating its lookups as
 * `readonly TimeHorizon[]` / `Record<Priority, number>` so a divergence between these literals
 * and the table's is a compile error rather than a discovery.
 *
 * No runtime dependency, and a browser imports this.
 */

/**
 * The three planning buckets, in the order a family is shown them.
 *
 * Nearest first. It is `shaping.ts`'s `HORIZONS` and is the order `get_backlog`'s `groups` are
 * built in, which is what a host reads out.
 */
export const TIME_HORIZON_ORDER = ["today", "this_week", "later"] as const;

export type BacklogHorizon = (typeof TIME_HORIZON_ORDER)[number];

/**
 * The three priorities, **most urgent first** — which is what makes this an order rather than
 * a list, and why it is not the `low | medium | high` the schema spells.
 *
 * A rank is the index, so the array is the whole of the rule.
 */
export const PRIORITY_ORDER = ["high", "medium", "low"] as const;

export type BacklogPriority = (typeof PRIORITY_ORDER)[number];

/**
 * Enough of an item to rank it — a **structural** parameter, naming the three fields the
 * comparison reads and nothing else.
 *
 * The idiom is {@link endSession}'s: this package may not depend on `@pilotfish/db` for `Item`
 * (the AWS SDK comes with it, and a browser imports this file), and it must not restate the
 * table's row either. `dueDate` admits `null` as well as `undefined` because the two callers
 * spell an absent date differently — `Item.dueDate` is optional, and the panel's read
 * discloses `dueDate: string | null` — and "there is no date" is the same fact in both.
 */
export interface RankableItem {
  readonly priority: BacklogPriority;
  readonly dueDate?: string | null;
  /** ISO instant, or anything else that sorts lexically by time — a ULID does too. */
  readonly createdAt: string;
}

/**
 * The rank of each priority — **derived from {@link PRIORITY_ORDER}, not written out beside
 * it**, so the array above really is the whole of the rule rather than a comment on a second
 * table that could disagree with it.
 */
const RANK: Readonly<Record<BacklogPriority, number>> = Object.fromEntries(
  PRIORITY_ORDER.map((priority, index) => [priority, index]),
) as Record<BacklogPriority, number>;

/**
 * Rank within one bucket: **priority desc → due date asc (undated last) → recency desc.**
 *
 * Deterministic and stable (ADR 0006 decision 4). Undated last rather than first because a
 * date is a reason to look at something sooner, and an item with no date is not thereby the
 * most urgent thing on the list.
 */
export function byBacklogRank(a: RankableItem, b: RankableItem): number {
  const priority = RANK[a.priority] - RANK[b.priority];
  if (priority !== 0) return priority;

  const dueA = a.dueDate ?? null;
  const dueB = b.dueDate ?? null;
  if (dueA !== dueB) {
    if (dueA === null) return 1;
    if (dueB === null) return -1;
    return dueA < dueB ? -1 : 1;
  }

  // More recent first — ULIDs and ISO timestamps both sort lexically by time.
  if (a.createdAt === b.createdAt) return 0;
  return a.createdAt > b.createdAt ? -1 : 1;
}
